import {FC} from "react";
import {useTranslation} from "react-i18next";
import {Alert} from "@interstate/components/Alert";
import {Box} from "@interstate/components/Box";
import {Spinner} from "@interstate/components/Spinner";
import {Typography} from "@interstate/components/Typography";
import {CopyButton} from "../../../action-buttons";
import {useTenancy} from "../hooks/useTenancy";
import "./TenantPanel.scss";

export interface TenantPanelProps {
  entityId: string;
  tenantId: string | undefined;
}

export const TenantPanel: FC<TenantPanelProps> = ({tenantId}) => {
  const {t} = useTranslation();
  const {tenant, loading, error} = useTenancy(tenantId);

  if (!tenantId) {
    return (
      <Alert
        id={"tenant-no-assignment"}
        data-testid={"tenant-no-assignment"}
        type={"caution"}
        title={t("tenancy.no-tenant.title")}>
        {t("tenancy.no-tenant.message")}
      </Alert>
    );
  }

  const renderName = () => {
    if (loading) {
      return (
        <Box className={"tenant-name-loading"}>
          <Spinner data-testid={"tenant-name-spinner"} size={"small"} />
          <Typography tag={"span"} variant={"body-sm"}>
            {t("tenancy.assignment.name-loading")}
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Typography
          tag={"span"}
          variant={"h3"}
          data-testid={"tenant-name-error"}
          color={"sem.color.on-surface.danger"}>
          {t("tenancy.assignment.name-error")}
        </Typography>
      );
    }

    return (
      <Typography
        tag={"span"}
        variant={"h3"}
        data-testid={"tenant-name-value"}
        className={"tenant-assignment-name"}>
        {tenant?.name}
      </Typography>
    );
  };

  return (
    <Box
      id={"tenant-assignment-panel"}
      data-testid={"tenant-assignment-panel"}
      className={"tenant-assignment-panel"}>
      {renderName()}
      <Box className={"tenant-assignment-field"}>
        <Typography tag={"span"} variant={"label-sm"} color={"sem.color.on-surface.muted"}>
          {t("tenancy.assignment.tenant-id")}
        </Typography>
        <Box className={"tenant-assignment-id-row"}>
          <Typography
            tag={"span"}
            variant={"body-sm"}
            data-testid={"tenant-id-value"}
            className={"tenant-assignment-id"}>
            {tenantId}
          </Typography>
          <CopyButton
            content={tenantId}
            qualifier={"tenant-id"}
            item={t("tenancy.assignment.tenant-id")}
            inline={true}
          />
        </Box>
      </Box>
    </Box>
  );
};
